import { Dialog as D } from 'radix-ui';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './utils';
import { Button } from './button';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** a document-sized dialog, e.g. a meeting */
  wide?: boolean;
}

/** Modal; full-screen sheet on phones. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
}: DialogProps) {
  const { t } = useTranslation();
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 animate-fade bg-scrim/35 backdrop-blur-[2px]" />
        <D.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[94dvh] animate-sheet flex-col rounded-t-[30px] bg-panel pb-[env(safe-area-inset-bottom)] shadow-pop focus:outline-none sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90vh] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:animate-pop sm:rounded-[28px] sm:pb-0',
            wide ? 'sm:w-[min(1000px,94vw)]' : 'sm:w-[min(640px,92vw)]',
          )}
          aria-describedby={description ? undefined : undefined}
        >
          <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-line sm:hidden" aria-hidden />
          <div className="flex items-center justify-between gap-3 px-6 pb-2 pt-3 sm:pt-5">
            <D.Title className="text-lg font-semibold tracking-tight sm:text-xl">{title}</D.Title>
            <D.Close asChild>
              <Button
                variant="secondary"
                size="icon"
                className="size-9"
                aria-label={t('common.close')}
              >
                <X />
              </Button>
            </D.Close>
          </div>
          {description ? (
            <D.Description className="px-6 pt-2 text-sm text-muted">{description}</D.Description>
          ) : (
            <D.Description className="sr-only">{title}</D.Description>
          )}
          <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-6 pt-2">{children}</div>
          {footer && (
            <div className="flex flex-wrap justify-end gap-2 border-t border-line/70 bg-tile px-6 py-4 sm:rounded-b-[28px] [&>button]:max-sm:flex-1">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: ReactNode;
  onConfirm: () => void;
  pending?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}
